import { useMutation, useQueryClient } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';
import { t } from '@lingui/core/macro';
import type { ViewMode, BookmarkCollectionResponse } from '../types/api';
import { useToast } from '../hooks/useToast';
import { dataHeaders } from './apiClient';

export type BookmarkReorderMode = 'unread' | 'archive' | 'starred';

export interface ReorderBookmarkParams {
  bookmarkId: string;
  beforeId: string | null;
  afterId: string | null;
  mode: BookmarkReorderMode;
  /** Full visual order of bookmark IDs after the move (for optimistic cache update). */
  orderedBookmarkIds: string[];
}

const REORDERABLE_VIEW_MODES: ReadonlySet<ViewMode> = new Set([
  'home',
  'unread',
  'folder',
  'archive',
  'liked',
]);

export function isBookmarkReorderEnabled(mode: ViewMode | undefined): boolean {
  return mode != null && REORDERABLE_VIEW_MODES.has(mode);
}

export function viewModeToReorderMode(mode: ViewMode): BookmarkReorderMode | undefined {
  if (mode === 'home' || mode === 'unread' || mode === 'folder') {
    return 'unread';
  }
  if (mode === 'archive') {
    return 'archive';
  }
  if (mode === 'liked') {
    return 'starred';
  }
  return undefined;
}

/**
 * Build before_id / after_id for Bookmark.reorder from a post-move visual list.
 *
 * Matches legacy CoffeeScript: after_id = visual neighbor above, before_id =
 * visual neighbor below; when sort is oldest, those are swapped so the API
 * still receives time-order neighbors.
 */
export function neighborsForReorder(
  orderedIds: string[],
  movedId: string,
  sortOrder: 'newest' | 'oldest',
): { beforeId: string | null; afterId: string | null } {
  const index = orderedIds.findIndex((id) => id === movedId);
  if (index === -1) {
    return { beforeId: null, afterId: null };
  }

  const visualAbove = index > 0 ? orderedIds[index - 1] : null;
  const visualBelow = index < orderedIds.length - 1 ? orderedIds[index + 1] : null;

  let beforeId = visualBelow;
  let afterId = visualAbove;

  if (sortOrder === 'oldest') {
    const tmp = beforeId;
    beforeId = afterId;
    afterId = tmp;
  }

  return { beforeId, afterId };
}

function bookmarkIdsMatch(id1: string | number, id2: string | number): boolean {
  return String(id1) === String(id2);
}

export function sortableBookmarkId(bookmarkId: string | number): string {
  return `bookmark-${bookmarkId}`;
}

export function parseSortableBookmarkId(sortableId: string | number): string {
  const raw = String(sortableId);
  return raw.startsWith('bookmark-') ? raw.slice('bookmark-'.length) : raw;
}

export async function reorderBookmark({
  bookmarkId,
  beforeId,
  afterId,
  mode,
}: Omit<ReorderBookmarkParams, 'orderedBookmarkIds'>): Promise<void> {
  const response = await fetch('/data/bookmarks/reorder', {
    method: 'POST',
    headers: dataHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'same-origin',
    body: JSON.stringify({
      bookmark_id: bookmarkId,
      before_id: beforeId,
      after_id: afterId,
      mode,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Failed to reorder bookmark: ${response.status}`);
  }
}

export function useReorderBookmark() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({
      orderedBookmarkIds: _orderedBookmarkIds,
      ...params
    }: ReorderBookmarkParams) => reorderBookmark(params),
    onMutate: async ({ bookmarkId, orderedBookmarkIds }) => {
      await queryClient.cancelQueries({ queryKey: ['bookmarks'] });

      const previousBookmarkLists = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === 'bookmarks';
        },
      });

      previousBookmarkLists.forEach(([queryKey]) => {
        queryClient.setQueryData(
          queryKey,
          (old: BookmarkCollectionResponse | undefined) => {
            if (!old?.bookmarks?.length) return old;

            const currentIds = old.bookmarks.map((b) => String(b.id));
            const containsMoved = currentIds.some((id) =>
              bookmarkIdsMatch(id, bookmarkId),
            );
            if (!containsMoved) return old;

            // Only reorder when this cache page matches the ordered set we moved within
            // (same length and same members). Avoid applying a page-local reorder to a
            // different section query.
            if (
              currentIds.length !== orderedBookmarkIds.length ||
              !orderedBookmarkIds.every((id: string) =>
                currentIds.some((currentId) => bookmarkIdsMatch(currentId, id)),
              )
            ) {
              return old;
            }

            const byId = new Map(
              old.bookmarks.map((bookmark) => [String(bookmark.id), bookmark]),
            );
            const reordered = orderedBookmarkIds
              .map((id: string) => byId.get(id))
              .filter((bookmark): bookmark is NonNullable<typeof bookmark> =>
                Boolean(bookmark),
              );

            if (reordered.length !== old.bookmarks.length) return old;
            return { ...old, bookmarks: reordered };
          },
        );
      });

      return { previousBookmarkLists };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousBookmarkLists) {
        context.previousBookmarkLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

export function computeReorderedIds(
  bookmarkIds: string[],
  activeId: string,
  overId: string,
): string[] | null {
  const oldIndex = bookmarkIds.findIndex((id) => id === activeId);
  const newIndex = bookmarkIds.findIndex((id) => id === overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return null;
  }
  return arrayMove(bookmarkIds, oldIndex, newIndex);
}
